/**
 * Razorpay Payment Button Component
 * Integrates with biogas transaction history for payments
 */

import React, { useState } from 'react';
import { CreditCard, Loader2, CheckCircle, XCircle, Edit2, X } from 'lucide-react';
import {
  loadRazorpayScript,
  createOrder,
  verifyPayment,
  displayRazorpayCheckout,
  type RazorpayOrderRequest,
  type RazorpayPaymentVerificationRequest,
} from '../../services/razorpayService';

interface RazorpayPaymentButtonProps {
  transactionId: number;
  amount: number;
  gaushalaName: string;
  gaushalaId?: number;
  description?: string;
  onSuccess: (paymentId: string, orderId: string, paidAmount?: number) => void;
  onFailure: (error: string) => void;
  disabled?: boolean;
  className?: string;
  allowAmountEdit?: boolean; // New prop to enable amount editing
}

export const RazorpayPaymentButton: React.FC<RazorpayPaymentButtonProps> = ({
  transactionId,
  amount,
  gaushalaName,
  gaushalaId,
  description,
  onSuccess,
  onFailure,
  disabled = false,
  className = '',
  allowAmountEdit = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState(amount);
  const [currentAmount, setCurrentAmount] = useState(amount);

  const handleAmountEdit = () => {
    setIsEditingAmount(true);
    setEditedAmount(currentAmount);
  };

  const handleAmountSave = () => {
    if (editedAmount > 0) {
      setCurrentAmount(editedAmount);
      setIsEditingAmount(false);
      setErrorMessage('');
    } else {
      setErrorMessage('Amount must be greater than zero');
    }
  };

  const handleAmountCancel = () => {
    setEditedAmount(currentAmount);
    setIsEditingAmount(false);
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      setPaymentStatus('processing');
      setErrorMessage('');

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load Razorpay SDK');
      }

      // Create order with current (possibly edited) amount
      const orderRequest: RazorpayOrderRequest = {
        transactionId,
        amount: currentAmount,
        gaushalaName,
        gaushalaId,
        description: description || `Payment for transaction #${transactionId}`,
      };

      const orderResponse = await createOrder(orderRequest);

      if (!orderResponse.success || !orderResponse.data) {
        throw new Error(orderResponse.error || 'Failed to create payment order');
      }

      const orderData = orderResponse.data;

      // Display Razorpay checkout
      displayRazorpayCheckout(
        {
          orderId: orderData.orderId,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'SAUBHAGYA Biogas',
          description: orderRequest.description,
          keyId: orderData.razorpayKeyId,
          prefill: {
            name: gaushalaName,
          },
          notes: {
            transactionId: transactionId.toString(),
            gaushalaName,
          },
        },
        // On success
        async (response) => {
          try {
            // Verify payment
            const verificationRequest: RazorpayPaymentVerificationRequest = {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              transactionId,
            };

            const verificationResponse = await verifyPayment(verificationRequest);

            if (verificationResponse.success && verificationResponse.data) {
              setPaymentStatus('success');
              // Pass the current amount (which may have been edited) to the success callback
              onSuccess(
                verificationResponse.data.paymentId,
                verificationResponse.data.orderId,
                currentAmount
              );
            } else {
              throw new Error(verificationResponse.error || 'Payment verification failed');
            }
          } catch (verifyError) {
            const message = verifyError instanceof Error ? verifyError.message : 'Payment verification failed';
            setPaymentStatus('failed');
            setErrorMessage(message);
            onFailure(message);
          } finally {
            setIsLoading(false);
          }
        },
        // On failure
        (error) => {
          const message = error.description || error.message || 'Payment failed';
          setPaymentStatus('failed');
          setErrorMessage(message);
          setIsLoading(false);
          onFailure(message);
        }
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to initiate payment';
      setPaymentStatus('failed');
      setErrorMessage(message);
      setIsLoading(false);
      onFailure(message);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Amount Editor */}
      {allowAmountEdit && isEditingAmount ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
          <span className="text-sm font-medium text-gray-700">₹</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={editedAmount}
            onChange={(e) => setEditedAmount(parseFloat(e.target.value) || 0)}
            className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
          <button
            onClick={handleAmountSave}
            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
            title="Save amount"
          >
            Save
          </button>
          <button
            onClick={handleAmountCancel}
            className="px-2 py-1 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={handlePayment}
            disabled={disabled || isLoading}
            className={`
              flex-1 inline-flex items-center justify-center gap-2 px-4 py-2
              rounded-lg font-medium transition-all duration-200
              ${disabled || isLoading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 active:scale-95'
              }
              ${className}
            `}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : paymentStatus === 'success' ? (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Payment Successful</span>
              </>
            ) : paymentStatus === 'failed' ? (
              <>
                <XCircle className="w-4 h-4" />
                <span>Payment Failed</span>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                <span>Pay ₹{currentAmount.toFixed(2)}</span>
              </>
            )}
          </button>

          {/* Edit Amount Button */}
          {allowAmountEdit && paymentStatus === 'idle' && !isLoading && (
            <button
              onClick={handleAmountEdit}
              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-all"
              title="Edit amount"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {errorMessage && (
        <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded border border-red-200">
          {errorMessage}
        </div>
      )}

      {paymentStatus === 'success' && (
        <div className="text-xs text-green-600 bg-green-50 px-3 py-2 rounded border border-green-200">
          Payment completed successfully!
        </div>
      )}
    </div>
  );
};

export default RazorpayPaymentButton;
